import { TreeNode, Position } from './types';
import { TREE_CONFIG } from '@/config';

export function calculateTreeLayout(
  nodes: TreeNode[],
  nodeWidth: number,
  nodeHeight: number,
  levelHeight: number,
  initialPadding: number
): Map<string, Position> {
  const positions = new Map<string, Position>();
  const nodeMap = new Map(nodes.map(node => [node.id, node]));

  // Find root nodes (nodes without parents)
  const rootNodes = nodes.filter(node => 
    !nodes.some(n => n.children?.includes(node.id))
  );

  function calculateSubtreeWidth(nodeId: string): number {
    const node = nodeMap.get(nodeId);
    if (!node || !node.children?.length) return nodeWidth;

    const childrenWidths = node.children.map(calculateSubtreeWidth);
    const totalChildrenWidth = childrenWidths.reduce((sum, width) => sum + width, 0);
    const totalSpacing = (node.children.length - 1) * TREE_CONFIG.SIBLING_SPACING;
    
    return Math.max(nodeWidth, totalChildrenWidth + totalSpacing);
  }

  function positionSubtree(nodeId: string, x: number, y: number) {
    const node = nodeMap.get(nodeId);
    if (!node) return;

    const subtreeWidth = calculateSubtreeWidth(nodeId);
    const nodeX = x + (subtreeWidth - nodeWidth) / 2;
    
    positions.set(nodeId, { x: nodeX, y });

    if (node.children?.length) {
      let childX = x;
      node.children.forEach(childId => {
        const childWidth = calculateSubtreeWidth(childId);
        positionSubtree(childId, childX, y + levelHeight);
        childX += childWidth + TREE_CONFIG.SIBLING_SPACING;
      });
    }
  }

  let currentX = initialPadding;
  rootNodes.forEach(root => {
    positionSubtree(root.id, currentX, initialPadding);
    currentX += calculateSubtreeWidth(root.id) + TREE_CONFIG.SIBLING_SPACING;
  });

  return positions;
}
